import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { LayeredDonut, LayeredDonutRing } from './LayeredDonut';

interface PerformanceToPlanDonutProps {
  p2pPercent?: number;   // Performance to Plan % (broker own / branch MP / company admin)
  paceToPlan?: number;   // Pace to Plan % — sourced from analytics cache p2p_pace field
}

// Legend order: Pace first, Performance to Plan second.
const METRICS = [
  { label: 'Pace to Plan',        color: '#2c5282' },
  { label: 'Performance to Plan', color: '#f97316' },
];

export const PerformanceToPlanDonut: React.FC<PerformanceToPlanDonutProps> = ({
  p2pPercent = 0,
  paceToPlan = 0,
}) => {
  const MAX_PCT = 150;

  // Actual values shown in the legend — uncapped
  const p2pActual  = Math.round(p2pPercent);
  const paceActual = Math.round(paceToPlan);

  // Ring fill capped at 150% so the arc never overflows
  const p2pRingVal  = Math.round((Math.min(MAX_PCT, Math.max(0, p2pActual))  / MAX_PCT) * 100);
  const paceRingVal = Math.round((Math.min(MAX_PCT, Math.max(0, paceActual)) / MAX_PCT) * 100);

  const hasData = p2pPercent > 0 || paceToPlan > 0;

  const rings: LayeredDonutRing[] = [
    { value: 100,         color: '#FFFFFF', isDecorative: true },
    { value: p2pRingVal,  color: '#f97316', label: 'Performance to Plan', displayValue: hasData ? `${p2pActual}%`  : '—' },
    { value: paceRingVal, color: '#2c5282', label: 'Pace to Plan',        displayValue: hasData ? `${paceActual}%` : '—' },
  ];

  const legendItems = [
    { ...METRICS[0], pct: paceActual },
    { ...METRICS[1], pct: p2pActual  },
  ];

  return (
    <Flex direction="column" align="center" gap="8px" w="100%">
      {/* Donut — scales with viewport */}
      <Box maxW={{ base: '160px', hxl: '200px' }} w="100%" minW="0" mx="auto">
        <LayeredDonut
          rings={rings}
          centerLabel={hasData ? `${paceActual}%` : '—'}
          hollowPercent="50%"
          centerFontSize="lg"
        />
      </Box>

      {/* Legend — below the donut */}
      <Flex direction="column" gap="10px" w="100%" minW="0">
        {legendItems.map((item) => (
          <Flex
            key={item.label}
            justify="space-between"
            align="center"
            gap="8px"
          >
            <Text
              fontSize="sm"
              fontWeight="700"
              color={item.color}
              lineHeight="1.2"
              whiteSpace="nowrap"
            >
              {item.label}
            </Text>
            <Text
              fontSize="sm"
              fontWeight="700"
              color={item.color}
              flexShrink={0}
            >
              {hasData ? `${item.pct}%` : '—'}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};
